import {
    BUDGET_LINE_ID,
    BUDGET_LINE_STATUS_EMPTY,
    BUDGET_LINE_STATUS_OBSOLETE,
    VOTED_FIELD,
} from 'routes/BudgetModule/config/constants';
import { BudgetPhases, Phase } from 'business/constants';
import {
    BudgetProposalT,
    StatusesSettingsT,
} from 'routes/BudgetModule/config/types';
import { getFieldByExercice } from 'routes/BudgetModule/helpers/format';
import { getAggAmount } from 'routes/BudgetModule/helpers/phaseHelpers';
import { getAggStatus } from 'routes/BudgetModule/helpers/statusHelpers';
import { getAmountFieldForPhase } from 'routes/BudgetModule/shared/phases/helpers';
import { ColDef, GridApi, ValueGetterParams } from 'ag-grid-community';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { translateStatus } from 'ui/AgGridReact/formatters';

// This aggregation functions is not standard
// It's not like we are defining an aggFunc on an inscription row that will be displayed
// when inscriptions rows are grouped
// It is instead defined for all the inscriptions that belong to a budget line
export const getAggAmountImputations = (
    params: Partial<ValueGetterParams>,
    exercice: number,
    phase: BudgetPhases,
    statusesSettings: StatusesSettingsT,
): null | number => {
    // TODO: this is not optimal as we're already computing the rows in the Detail Grid!
    // can we access the children from this node? or maybe build the data differently (create the status column in data before)
    if (!params.data) return null;

    const { inscriptions }: { inscriptions: InscriptionT[] } = params.context;

    const budgetLineId =
        params.data[getFieldByExercice(BUDGET_LINE_ID, exercice)];
    const amountField = getAmountFieldForPhase(phase);

    return getAggAmount(
        amountField,
        inscriptions.filter(
            (inscription) => inscription[BUDGET_LINE_ID] === budgetLineId,
        ),
        phase,
        statusesSettings,
    );
};

/**
 * We want to put the empty and obsolete status at the end of the list, and sort the others by their translated value
 * Eg: "À arbitrer" > "À traiter" > ... > "Vide" > "Obsolète"
 * @param valueA status value
 * @param valueB status value
 */
export const sortImputationsByStatus = (
    statusA: string,
    statusB: string,
): number => {
    const statusSortOrder: Record<string, number> = {
        [BUDGET_LINE_STATUS_EMPTY]: 1,
        [BUDGET_LINE_STATUS_OBSOLETE]: 2,
    };
    const orderA = statusSortOrder[statusA] || 0;
    const orderB = statusSortOrder[statusB] || 0;

    if (orderA !== orderB) {
        return orderA - orderB;
    }

    // it means both are EMPTY or OBSOLETE
    if (orderA > 0) {
        return 0;
    }
    const translatedA = translateStatus(statusA);
    const translatedB = translateStatus(statusB);
    // this is more performant than using localeCompare
    const collator = new Intl.Collator('fr', { sensitivity: 'base' });
    return collator.compare(translatedA, translatedB);
};

// This aggregation functions is not standard
// It's not like we are defining an aggFunc on an inscription row that will be displayed
// when inscriptions rows are grouped
// It is instead defined for all the inscriptions that belong to a budget line
export const getAggStatusImputations = (
    params: Partial<ValueGetterParams>,
    budgetProposal: BudgetProposalT,
    statusesSettings: StatusesSettingsT,
    phasesSettings: Phase[],
): null | string => {
    // TODO: this is not optimal as we're already computing the rows in the Detail Grid!
    // can we access the children from this node? or maybe build the data differently (create the status column in data before)
    if (!params.data) return null;

    const { inscriptions }: { inscriptions: InscriptionT[] } = params.context;

    const { phase } = budgetProposal;

    const budgetLineId =
        params.data[
            getFieldByExercice(BUDGET_LINE_ID, budgetProposal.exercice)
        ];

    const hasBudgetLineForCurrentExercice = budgetLineId > 0;

    return getAggStatus(
        inscriptions.filter(
            (inscription) => inscription[BUDGET_LINE_ID] === budgetLineId,
        ),
        phase,
        statusesSettings,
        phasesSettings,
        hasBudgetLineForCurrentExercice,
        params.data.isAmpmetropoleBudgetable,
    );
};

export const getAggEvolution = (
    params: ValueGetterParams,
    gridApi: GridApi | null,
    budgetProposal: BudgetProposalT,
    calculateEvolutionMethod: FlowAnyFunction,
    t: TranslationT,
): number | null => {
    // we want to build evolution on the primitive amount for supplementary phase
    const totalAmountRequestedField =
        budgetProposal.phase === BudgetPhases.SUPPLEMENTARY_BUDGET
            ? getAmountFieldForPhase(BudgetPhases.VOTE)
            : getAmountFieldForPhase(budgetProposal.phase);
    const votedAmountField = VOTED_FIELD;

    if (params?.node?.aggData) {
        return calculateEvolutionMethod(
            params.node.aggData[totalAmountRequestedField],
            params.node.aggData[
                getFieldByExercice(
                    votedAmountField,
                    budgetProposal.exercice - 1,
                )
            ],
            t,
        );
    }

    if (params.data) {
        const colDefs: ColDef[] = gridApi?.getColumnDefs() || [];
        const amountRequestedCol = colDefs.find(
            (e: ColDef) => e.field === totalAmountRequestedField,
        );

        const amountRequestedField =
            typeof amountRequestedCol?.valueGetter === 'function'
                ? amountRequestedCol.valueGetter(params)
                : '';

        return calculateEvolutionMethod(
            amountRequestedField,
            params?.node?.data[
                getFieldByExercice(
                    votedAmountField,
                    budgetProposal.exercice - 1,
                )
            ],
            t,
        );
    }

    return null;
};
